import { FC, useState } from "react";
import { Box, CardNotification, Heading, Text } from "@foris/avocado-suite";
import { useAsyncAction } from "@common/hooks/useAsyncAction";
import { downloadBlob } from "@utils/downloadBlob";
import { type LocalesByCode } from "@utils/i18nCatalog";
import { updateCatalogFile, UPDATED_CATALOG_FILE_NAME } from "@utils/i18nCatalogUpdate";
import FileUploadButton from "../file-upload-button";
import UpdateReport from "../update-report";
import { XLSX_MIME } from "../../constants";
import styles from "../../i18nCatalog.module.scss";

const ERROR_MESSAGE =
  "No se pudo actualizar el archivo. Verifica que sea un catálogo con las 8 columnas y su hoja Glosario.";

/** Step 1: brings a catalog already in the client's hands up to date with the app. */
const UpdateCatalogSection: FC<{ locales: LocalesByCode }> = ({ locales }) => {
  const [fileName, setFileName] = useState<string>(null);

  const {
    run,
    isLoading,
    error,
    result: report,
  } = useAsyncAction(async (file: File) => {
    const { workbook, report: updateReport } = await updateCatalogFile(file, locales);
    const buffer = await workbook.xlsx.writeBuffer();

    downloadBlob(new Blob([buffer], { type: XLSX_MIME }), UPDATED_CATALOG_FILE_NAME);

    return updateReport;
  }, ERROR_MESSAGE);

  const handleFile = (file: File) => {
    setFileName(file.name);
    run(file);
  };

  return (
    <Box className={styles.i18nCatalog_card}>
      <div className={styles.i18nCatalog_cardHeader}>
        <Heading type="h3">1. Actualizar el catálogo</Heading>
        <Text type="sm">
          Pone el Excel al día con los textos que la app tiene hoy, sin pisar nada de lo que ya
          escribió el negocio.
        </Text>
      </div>

      <FileUploadButton
        fileName={fileName}
        isLoading={isLoading}
        label="Subir catálogo y actualizar"
        onFile={handleFile}
      />

      {!!error && (
        <CardNotification state="error" title="Error" outlined>
          <Text type="sm">{error}</Text>
        </CardNotification>
      )}

      {!!report && <UpdateReport report={report} />}
    </Box>
  );
};

export default UpdateCatalogSection;
